/**
 * Online play: a table on the droplet, up to eight seats.
 *
 * The client holds no game state of its own. Every screen below is a pure
 * function of the last `RoomView` the server sent, which is what makes a
 * reconnect indistinguishable from a slow frame — there is nothing local to
 * reconcile, so there is nothing to get wrong.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MAX_PLAYERS, MIN_PLAYERS, NAME_MAX, normalizeCode, type RoomView } from '@shared/protocol';
import { Table } from '../game/Table';
import { Check, Eyebrow, Field, Masthead, Opt, Page, cx, fmt } from '../ui/shell';
import { RoomClient, storedName, type ConnStatus } from '../net/socket';

/** A shared link deep-links straight to the join screen with the code filled in. */
function codeFromUrl(): string {
  const m = /^\/t\/([A-Za-z0-9]{4,6})\/?$/.exec(location.pathname);
  return m ? normalizeCode(m[1]!) : '';
}

export function RemoteGame({ onExit }: { onExit: () => void }) {
  const [status, setStatus] = useState<ConnStatus>({
    kind: 'naming',
    code: codeFromUrl() || null,
    error: null,
    suggestion: null,
  });
  const [view, setView] = useState<RoomView | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const clientRef = useRef<RoomClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new RoomClient({
      onView: setView,
      onStatus: setStatus,
      onError: (_code, message) => setToast(message),
      onJoined: (code) => {
        // So the address bar is shareable, and a refresh lands back at the table.
        history.replaceState(null, '', `/t/${code}`);
      },
    });
  }
  const client = clientRef.current;

  useEffect(() => {
    return () => {
      client.close();
      history.replaceState(null, '', '/');
    };
  }, [client]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const leave = useCallback(() => {
    client.close();
    history.replaceState(null, '', '/');
    onExit();
  }, [client, onExit]);

  if (status.kind === 'naming') {
    return <Naming status={status} onBegin={(intent, name, code) => client.begin(intent, name, code)} onExit={onExit} />;
  }

  if (status.kind === 'ended') {
    return <Ended status={status} onExit={leave} />;
  }

  if (!view) {
    return <Waiting status={status} onExit={leave} />;
  }

  return (
    <Page>
      {status.kind !== 'open' && <ConnBanner status={status} />}
      {toast && <div className="toast">{toast}</div>}
      {view.game && view.phase !== 'lobby' ? (
        view.phase === 'over' ? (
          <Over view={view} client={client} onExit={leave} />
        ) : (
          <Table
            view={view.game}
            youId={view.you.id}
            onRoll={() => client.send({ t: 'roll', throwId: view.game!.throwId })}
            onToggle={(index) => client.send({ t: 'toggle', throwId: view.game!.throwId, index })}
            onClear={() => client.send({ t: 'clear', throwId: view.game!.throwId })}
            onBank={() => client.send({ t: 'bank', throwId: view.game!.throwId })}
            onTakeCarry={() => client.send({ t: 'takeCarry', throwId: view.game!.throwId })}
            action={{ label: 'Leave', onClick: leave }}
            notice={<TableNotices view={view} client={client} />}
          />
        )
      ) : (
        <Lobby view={view} client={client} onExit={leave} />
      )}
    </Page>
  );
}

// ── naming ──────────────────────────────────────────────────────────────────

function Naming({
  status,
  onBegin,
  onExit,
}: {
  status: Extract<ConnStatus, { kind: 'naming' }>;
  onBegin: (intent: 'create' | 'join', name: string, code: string | null) => void;
  onExit: () => void;
}) {
  const [name, setName] = useState(() => status.suggestion ?? storedName());
  const [code, setCode] = useState(status.code ?? '');
  const [mode, setMode] = useState<'create' | 'join'>(status.code ? 'join' : 'create');

  const clean = name.trim().slice(0, NAME_MAX);
  const ready = clean.length > 0 && (mode === 'create' || normalizeCode(code).length >= 4);

  return (
    <Page>
      <Masthead sub="play with friends" />

      {status.error && (
        <div className="banner">
          <Eyebrow tone="ivory">{errorLine(status.error)}</Eyebrow>
        </div>
      )}

      <Field label="What should we call you">
        <input
          className="input name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={NAME_MAX}
          autoComplete="nickname"
          aria-label="Your name"
        />
      </Field>

      <div className="btn-row seg">
        {(
          [
            ['create', 'Start a table'],
            ['join', 'Join a table'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cx('seg-btn', mode === key && 'seg-btn--on')}
            onClick={() => setMode(key)}
            aria-pressed={mode === key}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'join' && (
        <Field label="Table code">
          <input
            className="input input--code"
            value={code}
            onChange={(e) => setCode(normalizeCode(e.target.value))}
            placeholder="ABCD"
            maxLength={6}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            aria-label="Table code"
          />
        </Field>
      )}

      <div className="spacer" />
      <div className="btn-stack">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!ready}
          onClick={() => onBegin(mode, clean, mode === 'join' ? normalizeCode(code) : null)}
        >
          {mode === 'create' ? 'START A TABLE' : 'SIT DOWN'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onExit}>
          Back
        </button>
      </div>
    </Page>
  );
}

function errorLine(code: string): string {
  switch (code) {
    case 'ROOM_NOT_FOUND':
      return "No table with that code";
    case 'NAME_TAKEN':
      return 'That name is taken at this table';
    case 'ROOM_FULL':
      return 'That table is full';
    default:
      return 'That did not work — try again';
  }
}

// ── lobby ───────────────────────────────────────────────────────────────────

function Lobby({ view, client, onExit }: { view: RoomView; client: RoomClient; onExit: () => void }) {
  const [copied, setCopied] = useState(false);
  const humans = view.players.filter((p) => !p.id.startsWith('bot'));
  const total = humans.length + view.settings.bots;
  const canStart = view.you.isHost && total >= MIN_PLAYERS && total <= MAX_PLAYERS;

  const share = async () => {
    const url = `${location.origin}/t/${view.code}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Farkle', text: `Join my Farkle table`, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* the user dismissed the sheet; nothing to do */
    }
  };

  const set = (patch: Record<string, unknown>) => client.send({ t: 'settings', settings: patch });

  return (
    <>
      <Masthead sub="waiting to start" />

      <div className="code-plate">
        <Eyebrow tone="dim">Table code</Eyebrow>
        <div className="code">{view.code}</div>
        <button type="button" className="btn btn--small share-btn" onClick={share}>
          {copied ? 'Link copied' : 'Share the link'}
        </button>
      </div>

      <Eyebrow className="field-label">At the table ({total})</Eyebrow>
      <div className="standings lobby-list">
        {humans.map((p) => (
          <div className="row" key={p.id}>
            <span className={cx('dot', !p.online && 'dot--off')} aria-hidden="true" />
            <span className="who">
              {p.name}
              {p.id === view.you.id ? ' (you)' : ''}
            </span>
            {p.isHost && <span className="tag">host</span>}
            {view.you.isHost && p.id !== view.you.id && (
              <button
                type="button"
                className="btn btn--small"
                onClick={() => client.send({ t: 'kick', playerId: p.id })}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {Array.from({ length: view.settings.bots }, (_, i) => (
          <div className="row" key={`bot${i}`}>
            <span className="dot" aria-hidden="true" />
            <span className="who">Computer {i + 1}</span>
            <span className="tag">cpu</span>
          </div>
        ))}
      </div>

      <div className="spacer" />

      {view.you.isHost ? (
        <>
          <Field label="Computer players">
            <div className="opt-row">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <Opt
                  key={n}
                  on={view.settings.bots === n}
                  onClick={() => set({ bots: n })}
                  label={`${n} computer players`}
                >
                  {n}
                </Opt>
              ))}
            </div>
          </Field>

          {view.settings.bots > 0 && (
            <Field label="How well they play">
              <div className="opt-row">
                {(
                  [
                    ['easy', 'Loose'],
                    ['steady', 'Steady'],
                    ['sharp', 'Sharp'],
                  ] as const
                ).map(([key, label]) => (
                  <Opt
                    key={key}
                    wide
                    on={view.settings.difficulty === key}
                    onClick={() => set({ difficulty: key })}
                  >
                    {label}
                  </Opt>
                ))}
              </div>
            </Field>
          )}

          <Field label="Play to">
            <div className="opt-row">
              {[2000, 5000, 10_000, 20_000].map((t) => (
                <Opt key={t} wide on={view.settings.target === t} onClick={() => set({ target: t })}>
                  {fmt(t)}
                </Opt>
              ))}
            </div>
          </Field>

          <Field label="Points needed to get on the board">
            <div className="opt-row">
              {[0, 350, 500, 1000].map((t) => (
                <Opt key={t} wide on={view.settings.threshold === t} onClick={() => set({ threshold: t })}>
                  {t === 0 ? 'None' : t}
                </Opt>
              ))}
            </div>
          </Field>

          <Check on={view.settings.finalRound} onClick={() => set({ finalRound: !view.settings.finalRound })}>
            Everyone else gets one last turn once someone hits the target
          </Check>

          <Check on={view.settings.piggyback} onClick={() => set({ piggyback: !view.settings.piggyback })}>
            Piggyback — if the player before you stops with dice left, you can throw their dice and start on their
            score
          </Check>

          <div className="spacer" />
          <div className="btn-stack">
            <button
              type="button"
              className="btn btn--primary"
              disabled={!canStart}
              onClick={() => client.send({ t: 'start' })}
            >
              {total < MIN_PLAYERS ? 'NEED ONE MORE' : 'START THE GAME'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={onExit}>
              Leave
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="well center settings-readout">
            <Eyebrow tone="dim">The house rules</Eyebrow>
            <div className="readout">
              Play to {fmt(view.settings.target)}
              {view.settings.threshold > 0 ? ` · ${fmt(view.settings.threshold)} to open` : ' · no opener'}
              {view.settings.finalRound ? ' · last turns' : ''}
              {view.settings.piggyback ? ' · piggyback' : ''}
            </div>
          </div>
          <div className="spacer" />
          <div className="center waiting-line">Waiting for the host to start…</div>
          <div className="spacer" />
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            Leave
          </button>
        </>
      )}
    </>
  );
}

// ── in-game notices ─────────────────────────────────────────────────────────

function TableNotices({ view, client }: { view: RoomView; client: RoomClient }) {
  const offline = view.players.filter((p) => !p.online && !p.id.startsWith('bot'));
  return (
    <>
      {view.hostChangedTo && (
        <div className="banner banner--brass">
          <Eyebrow>{view.hostChangedTo} is running the table now</Eyebrow>
        </div>
      )}
      {view.hostAction && (
        <div className="banner">
          <Eyebrow tone="ivory">{view.hostAction.names.join(', ')} dropped out</Eyebrow>
          <button
            type="button"
            className="btn btn--small hatch-btn"
            onClick={() => client.send({ t: 'kick', playerId: '' })}
          >
            Let the computer throw for them
          </button>
        </div>
      )}
      {offline.length > 0 && !view.hostAction && (
        <div className="banner banner--quiet">
          <Eyebrow tone="dim">{offline.map((p) => p.name).join(', ')} reconnecting…</Eyebrow>
        </div>
      )}
    </>
  );
}

// ── endings ─────────────────────────────────────────────────────────────────

function Over({ view, client, onExit }: { view: RoomView; client: RoomClient; onExit: () => void }) {
  const game = view.game!;
  const rankedList = useMemo(() => [...game.players].sort((a, b) => b.score - a.score), [game.players]);
  const top = rankedList[0]?.score ?? 0;
  const winners = rankedList.filter((p) => p.score === top);
  const youWon = winners.some((p) => p.id === view.you.id);

  return (
    <div className="rise">
      <Masthead sub={youWon ? 'you took it' : 'final tally'} />

      <div className="card center winner">
        <Eyebrow tone="blood">{winners.length > 1 ? 'Tied at the top' : 'Winner'}</Eyebrow>
        <div className="winner-name">
          {winners.map((w) => (w.id === view.you.id ? 'You' : w.name)).join(' & ')}
        </div>
        <div className="num winner-score">{fmt(top)}</div>
      </div>

      <div className="standings final-standings">
        {rankedList.map((p, i) => (
          <div className="row" key={p.id}>
            <span className="rank">{i + 1}</span>
            <span className="who">{p.id === view.you.id ? 'You' : p.name}</span>
            <span className="score">{fmt(p.score)}</span>
          </div>
        ))}
      </div>

      <div className="btn-stack">
        <button type="button" className="btn btn--primary" onClick={() => client.send({ t: 'again' })}>
          BACK TO THE LOBBY
        </button>
        <button type="button" className="btn btn--ghost" onClick={onExit}>
          Leave the table
        </button>
      </div>
    </div>
  );
}

function Ended({
  status,
  onExit,
}: {
  status: Extract<ConnStatus, { kind: 'ended' }>;
  onExit: () => void;
}) {
  const line =
    status.reason === 'removed'
      ? 'The host removed you from the table.'
      : status.reason === 'replaced'
        ? 'You opened this table in another tab. That one has the seat.'
        : 'That table is gone — everyone left, or it sat empty too long.';
  return (
    <Page>
      <Masthead sub="that's that" />
      <div className="well center ended-note">{line}</div>
      <div className="spacer" />
      <button type="button" className="btn btn--primary" onClick={onExit}>
        BACK TO THE MENU
      </button>
    </Page>
  );
}

function Waiting({ status, onExit }: { status: ConnStatus; onExit: () => void }) {
  return (
    <Page>
      <Masthead sub="one moment" />
      <div className="well center ended-note">{statusLine(status)}</div>
      <div className="spacer" />
      <button type="button" className="btn btn--ghost" onClick={onExit}>
        Back
      </button>
    </Page>
  );
}

function ConnBanner({ status }: { status: ConnStatus }) {
  return (
    <div className={cx('banner', status.kind === 'restarting' ? 'banner--brass' : 'banner--quiet')}>
      <Eyebrow tone={status.kind === 'restarting' ? undefined : 'dim'}>{statusLine(status)}</Eyebrow>
    </div>
  );
}

function statusLine(status: ConnStatus): string {
  switch (status.kind) {
    case 'connecting':
      return 'Finding the table…';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'restarting':
      return 'The game server is restarting — hold on, your seat is kept';
    case 'waitingForNextGame':
      return `That game is already going. You'll be seated for the next one.`;
    default:
      return 'Connecting…';
  }
}
